import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';

const DocumentStorageOverview = ({ dashboardData }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();

  // Extract storage data from API response
  const storageOverview = dashboardData?.document_storage_overview || {};
  const totalUploadedSizeMB = storageOverview.total_uploaded_size_mb || 0;
  const allocatedSizeMB = storageOverview.allocated_size_mb || 0;
  const usagePercentage = storageOverview.usage_percentage || 0;

  // Debug logs to check data
  console.log('DocumentStorageOverview - storageOverview:', storageOverview);
  console.log('DocumentStorageOverview - totalUploadedSizeMB:', totalUploadedSizeMB);
  console.log('DocumentStorageOverview - allocatedSizeMB:', allocatedSizeMB);
  console.log('DocumentStorageOverview - usagePercentage:', usagePercentage);

  // Calculate storage values
  const usedStorage = parseFloat(totalUploadedSizeMB.toFixed(2));
  const totalStorageMB = parseFloat(allocatedSizeMB.toFixed(2));
  const leftStorage = Math.max(0, totalStorageMB - usedStorage);

  // Convert to human‑readable units for header line
  const formatStorage = (mb) => {
    if (!mb && mb !== 0) return '0 MB';
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${Math.round(mb)} MB`;
  };

  const usedDisplay = formatStorage(usedStorage);
  const totalDisplay = formatStorage(totalStorageMB);

  // Clamp percentage between 0 and 100 for progress bar
  const safeUsage = Math.min(100, Math.max(0, usagePercentage || 0));

  return (
    <div 
      onClick={() => navigate('/documents')}
      style={{
        backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0',
        height: '280px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = mode === 'dark' 
          ? '0 4px 12px rgba(0, 0, 0, 0.5)' 
          : '0 4px 12px rgba(0, 0, 0, 0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Header */}
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        margin: '0 0 16px 0',
        color: mode === 'dark' ? '#ffffff' : '#333333',
        fontFamily: 'Urbanist, Arial, sans-serif'
      }}>
        Document Storage Overview
      </h3>

      {/* Top line: used of total */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'baseline',
        gap: '6px'
      }}>
        <span style={{
          fontSize: '24px',
          fontWeight: '700',
          color: mode === 'dark' ? '#ffffff' : '#111827',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {usedDisplay.split(' ')[0]} {usedDisplay.split(' ')[1]}
        </span>
        <span style={{
          fontSize: '14px',
          color: mode === 'dark' ? '#A1A1AA' : '#6B7280',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          used of {totalDisplay}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{
        width: '100%',
        height: '10px',
        borderRadius: '999px',
        backgroundColor: mode === 'dark' ? '#27272F' : '#E5E7EB',
        overflow: 'hidden',
        marginBottom: '20px'
      }}>
        <div
          style={{
            width: `${safeUsage}%`,
            height: '100%',
            borderRadius: '999px',
            backgroundColor: '#26c3c0',
            transition: 'width 0.3s ease'
          }}
        />
      </div>

      {/* Legend: used vs available */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#26c3c0'
          }}></div>
          <span style={{
            fontSize: '14px',
            color: mode === 'dark' ? '#E5E7EB' : '#111827',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            Used: {usedDisplay}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: mode === 'dark' ? '#3F3F46' : '#D4D4D8'
          }}></div>
          <span style={{
            fontSize: '14px',
            color: mode === 'dark' ? '#E5E7EB' : '#111827',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            Available: {formatStorage(leftStorage)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DocumentStorageOverview;
